import React from 'react';

interface DateTimeFormatterProps {
    date: string | Date;
    format?: 'datetime' | 'date' | 'time';
    locale?: string;
    /** IANA timezone (e.g. from system config) for CRM; omit for trader views / browser local */
    timeZone?: string;
    className?: string;
    showIcon?: boolean;
    icon?: React.ReactNode;
}

/**
 * Reusable DateTimeFormatter component.
 * Default format: dd.MM.yyyy HH:mm (24h format).
 * API returns datetimes in UTC (ISO with Z); pass timeZone in CRM to display in tenant timezone.
 */
export const DateTimeFormatter: React.FC<DateTimeFormatterProps> = ({
    date,
    format = 'datetime',
    locale = 'en-GB', // Use en-GB for dd.MM.yyyy format
    timeZone,
    className = '',
    showIcon = false,
    icon,
}) => {
    const formatDateTime = (dateValue: string | Date) => {
        const dateObj = new Date(dateValue);

        if (isNaN(dateObj.getTime())) {
            return 'Invalid Date';
        }

        const options: Intl.DateTimeFormatOptions = {};

        switch (format) {
            case 'date':
                options.day = '2-digit';
                options.month = '2-digit';
                options.year = 'numeric';
                break;
            case 'time':
                options.hour = '2-digit';
                options.minute = '2-digit';
                options.hour12 = false; // 24h format
                break;
            case 'datetime':
            default:
                options.day = '2-digit';
                options.month = '2-digit';
                options.year = 'numeric';
                options.hour = '2-digit';
                options.minute = '2-digit';
                options.hour12 = false; // 24h format
                break;
        }

        if (timeZone) {
            options.timeZone = timeZone;
        }

        return dateObj.toLocaleString(locale, options);
    };

    const formattedDate = formatDateTime(date);

    if (showIcon && icon) {
        return (
            <div className={`flex items-center gap-1 ${className}`}>
                {icon}
                <span>{formattedDate}</span>
            </div>
        );
    }

    return <span className={className}>{formattedDate}</span>;
};

export default DateTimeFormatter;
